import * as React from "react";

import MerchantDetailTopBar from "./Ops.Merchants.MerchantDetailTopBar";
import gql from "graphql-tag";
import { graphql, compose } from "react-apollo";
import { match } from "react-router-dom";
import handleMutationError from "app/utils/handleMutationError";
import { withStyles } from "@material-ui/core/styles";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Snackbar from "@material-ui/core/Snackbar";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";

type Props = {
  classes: any;
  match: match<{ id: string }>;
  query: () => void;
  updateMerchantTags: (args: {
    variables: { id: any; tags?: string[]; internal_tags?: string[] };
  }) => Promise<any>;
  data: any;
};

type State = {
  data: any;
  isLoading: boolean;
  internalOptions: Array<TagOption>;
  internalValue: Array<TagOption>;
  options: Array<TagOption>;
  value: Array<TagOption>;
  snackbarOpen: boolean;
  snackbarMessage: string;
};

type TagOption = {
  label: string;
  value: string;
};

const styles = theme => ({
  close: {
    padding: theme.spacing.unit / 2
  },
  root: {
    background: "#fafafa"
  },
  gridContainer: {
    padding: theme.spacing.unit * 4
  }
});

class Tags extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);

    this.state = {
      data: null,
      isLoading: false,
      internalOptions: [],
      internalValue: [],
      options: [],
      value: [],
      snackbarOpen: false,
      snackbarMessage: ""
    };
  }

  // tagType is the graphQL field name
  getTagsList = (
    tagType: string = "all_available_tags",
    sorted: boolean = false
  ): Array<TagOption> => {
    let allTags: Array<string> =
      this.props.data && this.props.data.merchant
        ? this.props.data.merchant[tagType]
        : [];
    let mappedTags: Array<TagOption> = allTags.map(tag => {
      return {
        label: tag,
        value: tag
      };
    });
    if (sorted) {
      mappedTags = mappedTags.sort(function(a: any, b: any) {
        return a.label.localeCompare(b.label);
      });
    }
    return mappedTags;
  };

  handleTagsChange = (newTagList: Array<TagOption>, tagType: string) => {
    const snackbarMessage = "Tags have been updated";
    const tagList: Array<string> = (newTagList || []).map(tag => {
      return tag["value"];
    });

    this.updateTags(tagList, tagType);
    this.openSnackbar({ snackbarMessage });
  };

  createNewTag = (newTag: string) => {
    const snackbarMessage = "New tag created";
    this.openSnackbar({ snackbarMessage });
    let tags: string[] = this.props?.data?.merchant?.tags ?? [];
    tags = tags.concat(newTag);
    this.updateTags(tags, "tags");
  };

  updateTags = (tags: Array<string>, tagType: string) => {
    this.setState({ isLoading: true });
    return this.props
      .updateMerchantTags({
        variables: { id: this.props.match.params.id, [tagType]: tags }
      })
      .then(() => {
        this.props.data.refetch();
      })
      .catch(handleMutationError)
      .finally(() => {
        this.setState({
          isLoading: false
        });
      });
  };

  openSnackbar = ({ snackbarMessage }) => {
    this.setState({ snackbarOpen: true, snackbarMessage });
  };

  closeSnackbar = () => {
    this.setState({
      snackbarOpen: false,
      snackbarMessage: ""
    });
  };

  render() {
    const { classes } = this.props;
    const { isLoading, snackbarMessage } = this.state;
    const snackbarMessageHtml = (
      <span id="snackbar-message-id">{snackbarMessage}</span>
    );
    return (
      <section className={classes.root}>
        <MerchantDetailTopBar />
        <Grid
          container
          className={classes.gridContainer}
          spacing={16}
          justify="center"
        >
          <Grid item xs={12} sm={10} lg={6}>
            <Paper elevation={1} style={{ padding: "10px" }}>
              <Typography
                variant="h5"
                component="h5"
                style={{ marginBottom: "10px" }}
              >
                Internal tags
              </Typography>
              <Select
                isMulti="1"
                isClearable={false}
                isDisabled={isLoading}
                isLoading={isLoading}
                onChange={(ar: Array<TagOption>) =>
                  this.handleTagsChange(ar, "internal_tags")
                }
                options={this.getTagsList("all_available_internal_tags", true)}
                value={this.getTagsList("internal_tags", true)}
                placeholder="Internal Tags"
              />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={10} lg={6}>
            <Paper elevation={1} style={{ padding: "10px" }}>
              <Typography variant="h5" component="h5" gutterBottom>
                Merchant tags
              </Typography>
              <CreatableSelect
                isMulti="1"
                isClearable={false}
                isDisabled={isLoading}
                isLoading={isLoading}
                onChange={(ar: Array<TagOption>) =>
                  this.handleTagsChange(ar, "tags")
                }
                onCreateOption={this.createNewTag}
                options={this.getTagsList("all_available_tags", true)}
                value={this.getTagsList("tags", true)}
                placeholder="Merchant Tags"
              />
            </Paper>
          </Grid>
        </Grid>
        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          open={this.state.snackbarOpen}
          autoHideDuration={6000}
          onClose={this.closeSnackbar}
          ContentProps={{
            "aria-describedby": "message-id"
          }}
          message={snackbarMessageHtml}
          action={[
            <IconButton
              key="close"
              aria-label="Close"
              color="inherit"
              onClick={this.closeSnackbar}
            >
              <CloseIcon />
            </IconButton>
          ]}
        />
      </section>
    );
  }
}

const updateTags = graphql(
  gql`
    mutation updateMerchantTags(
      $id: ID!
      $tags: [String]
      $internal_tags: [String]
    ) {
      update_merchant(id: $id, tags: $tags, internal_tags: $internal_tags) {
        tags
      }
    }
  `,
  {
    name: "updateMerchantTags"
  }
);

const queryTags = graphql(
  gql`
    query Merchant($id: ID!) {
      merchant(id: $id) {
        id
        tags
        all_available_tags
        internal_tags
        all_available_internal_tags
      }
    }
  `,
  {
    options(props: any) {
      return {
        variables: {
          id: props.match.params.id
        }
      };
    }
  }
);

export default compose<any>(updateTags, queryTags)(withStyles(styles)(Tags));
